(() => {
  if (window.HermeticaHushGiftOffer) return;

  let config;
  try {
    config = JSON.parse(document.getElementById('hsf-hush-gift-config')?.textContent || 'null');
  } catch (_) {
    return;
  }
  // A missing or malformed contract must never cause automatic cart additions.
  if (
    config?.schemaVersion !== 1 ||
    typeof config.enabled !== 'boolean' ||
    !config.id || !config.discountTitle || !config.hushHandle ||
    !Number.isSafeInteger(config.hushProductId) ||
    (config.hushBundleHandle !== undefined && (typeof config.hushBundleHandle !== 'string' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(config.hushBundleHandle))) ||
    (config.hushBundleProductId !== undefined && (!Number.isSafeInteger(config.hushBundleProductId) || config.hushBundleProductId <= 0)) ||
    !config.eligibility ||
    !Array.isArray(config.gifts) || config.gifts.length < 1 || config.gifts.length > 6 ||
    config.gifts.some((gift) => !gift.role || !Number.isSafeInteger(gift.variantId)) ||
    new Set(config.gifts.map((gift) => gift.role)).size !== config.gifts.length ||
    new Set(config.gifts.map((gift) => gift.variantId)).size !== config.gifts.length
  ) return;
  const offer = Object.freeze({
    ...config,
    startsAt: Date.parse(config.startsAt),
    endsAt: config.endsAt === null ? null : Date.parse(config.endsAt),
    eligibility: Object.freeze({ ...config.eligibility }),
    gifts: Object.freeze(config.gifts.map((gift) => Object.freeze({ ...gift })))
  });
  const hushHandles = new Set([offer.hushHandle, offer.hushBundleHandle].filter(Boolean));
  const hushProductIds = [offer.hushProductId, offer.hushBundleProductId].filter((id) => Number.isSafeInteger(id) && id > 0);
  const isHushLine = (item) => hushHandles.has(item?.handle);
  const source = 'hsf-gift-offer';
  const sessionKey = 'hsf_gift_offer_session';
  const viewedKey = 'hsf_gift_offer_views';
  const unavailableKey = 'hsf_gift_offer_unavailable';
  const currency = window.Shopify?.currency?.active || 'USD';
  const analyticsEnabled = window.HermeticaHushGiftOfferAnalyticsEnabled !== false;
  let reconciliationTail = Promise.resolve();
  let eventTimer = 0;
  let viewMutationTimer = 0;
  let wasEligible = false;
  let pendingReconciliations = 0;
  let lastCart = null;
  let checkoutAttempt = false;
  let pendingNativeMutations = 0;
  let nativeMutationRevision = 0;
  let latestNoteDraft = null;
  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const newId = () => {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    } catch (_) {}
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const sessionId = (() => {
    try {
      let value = sessionStorage.getItem(sessionKey);
      if (!value) {
        value = newId();
        sessionStorage.setItem(sessionKey, value);
      }
      return value;
    } catch (_) {
      return 'session-unavailable';
    }
  })();

  const emit = (name, params = {}) => {
    if (!analyticsEnabled) return;
    const eventName = `hsf_${name}`;
    const payload = {
      event_id: newId(),
      session_id: sessionId,
      offer_id: offer.id,
      currency,
      ...params
    };
    try {
      if (!window.dataLayer || typeof window.dataLayer.push !== 'function') window.dataLayer = [];
      window.dataLayer.push({ event: eventName, ...payload });
    } catch (_) {}
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, { ...payload, send_to: 'G-R5GK4CN152', transport_type: 'beacon' });
      }
    } catch (_) {}
  };

  // A stalled cart request must release the UI for a retry, never bypass verification.
  const cartRequest = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (!response.ok) {
        throw new Error(url === '/cart/add.js' && response.status === 422 ? 'gift_unavailable' : 'cart_request_failed');
      }
      return await response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const fetchCart = () => cartRequest('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });

  const changeLine = (key, quantity) => cartRequest('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity })
    });

  const addGifts = (gifts) => cartRequest('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: gifts.map((gift) => ({
          id: gift.variantId,
          quantity: 1,
          properties: {
            _hsf_gift_offer: offer.id,
            _hsf_gift_role: gift.role
          }
        }))
      })
    });

  const properties = (item) => item?.properties || {};
  const isTaggedGift = (item) => Boolean(properties(item)._hsf_gift_offer);
  const giftRole = (item) => properties(item)._hsf_gift_role || '';
  const isOfferActive = () =>
    offer.enabled &&
    Number.isFinite(offer.startsAt) && Date.now() >= offer.startsAt &&
    (offer.endsAt === null || (Number.isFinite(offer.endsAt) && Date.now() < offer.endsAt));
  const cartState = (cart) => ({
    cart_item_count: Number(cart?.item_count || 0),
    cart_total: Number(cart?.total_price || 0) / 100
  });
  const purchaseType = (cart, fallback = '') => {
    if (fallback) return fallback;
    const hushLines = (cart?.items || []).filter((item) => isHushLine(item) && !isTaggedGift(item));
    if (!hushLines.length) return 'none';
    return hushLines.some((item) => item.selling_plan_allocation) ? 'subscription' : 'one_time';
  };
  const hasHush = (cart) =>
    (cart?.items || []).some(
      (item) =>
        isHushLine(item) &&
        !isTaggedGift(item) &&
        properties(item)._hsf_free_single_code !== 'FREEGIFT' &&
        Number(item.quantity || 0) > 0
    );
  // The shopper pressed "No thanks" in the cart. Stored on the cart itself so the
  // cart page, this script and checkout all agree, and it survives a reload.
  const declineAttribute = '_hsf_gift_declined';
  const giftDeclined = (cart) => cart?.attributes?.[declineAttribute] === offer.id;
  const isEligible = (cart, context = {}) => {
    if (!isOfferActive() || !hasHush(cart) || giftDeclined(cart)) return false;
    if (context.purchase_type === 'subscription_renewal') return offer.eligibility.subscriptionRenewals === true;
    return (cart?.items || []).some((item) => {
      if (!isHushLine(item) || isTaggedGift(item) || Number(item.quantity || 0) <= 0) return false;
      return item.selling_plan_allocation
        ? offer.eligibility.initialSubscriptionCheckout === true
        : offer.eligibility.oneTimeCheckout === true;
    });
  };
  const taggedItems = (cart) => (cart?.items || []).filter(isTaggedGift);
  const isCartSurface = () => /^\/cart(?:\/|$)/.test(location.pathname);
  const isHushSurface = () =>
    Array.from(hushHandles).some((handle) => new RegExp(`^/products/${handle}(?:/|$)`).test(location.pathname));
  const itemsForGift = (cart, gift) =>
    (cart?.items || []).filter(
      (item) => Number(item.variant_id) === gift.variantId && Number(item.quantity || 0) > 0
    );
  const taggedLineIsFree = (item) =>
    item.final_line_price != null && Number.isFinite(Number(item.final_line_price)) && Number(item.final_line_price) === 0 &&
    Number(item.quantity || 0) === 1;
  const cartSafeForCheckout = (cart) =>
    Array.isArray(cart?.items) && taggedItems(cart).every(taggedLineIsFree);
  const giftRelevantCart = (cart) => Boolean(cart && (hasHush(cart) || taggedItems(cart).length));
  const updateCheckoutControls = () => {
    const checking = pendingReconciliations > 0 || pendingNativeMutations > 0 || checkoutAttempt;
    document.querySelectorAll('[data-hsf-gift-checkout]').forEach((button) => {
      // Liquid blocks unsafe paid gifts before JS loads. The guarded button can then
      // be used to retry; a failed verification does not permanently disable it.
      if (button.hasAttribute('data-hsf-gift-server-blocked')) {
        button.removeAttribute('data-hsf-gift-server-blocked');
        if (button.dataset.hsfCartEmpty !== 'true') button.disabled = false;
      }
      button.setAttribute('aria-busy', String(checking));
    });
    document.querySelectorAll('[data-hsf-gift-accelerated]').forEach((node) => {
      // Express checkout can live in a closed shadow root and cannot be safely
      // intercepted. Hush/gift carts use the guarded native checkout instead.
      const blocked = checking || giftRelevantCart(lastCart) || node.dataset.hsfGiftRelevant === 'true';
      node.hidden = blocked;
      node.inert = blocked;
      node.style.display = blocked ? 'none' : '';
    });
  };
  const checkoutMessage = (message) => {
    document.querySelectorAll('[data-hsf-gift-checkout-status]').forEach((node) => {
      node.textContent = message;
      node.hidden = !message;
    });
  };
  const beginCartMutation = (context = {}) => {
    if (typeof context.note === 'string') latestNoteDraft = context.note;
    pendingNativeMutations += 1;
    nativeMutationRevision += 1;
    updateCheckoutControls();
    let finished = false;
    return () => {
      if (finished) return;
      finished = true;
      pendingNativeMutations -= 1;
      nativeMutationRevision += 1;
      updateCheckoutControls();
    };
  };
  const waitForNativeCart = async () => {
    for (let attempt = 0; attempt < 160; attempt += 1) {
      if (!pendingNativeMutations && !document.querySelector('cart-items-component.cart-items-disabled')) return true;
      await wait(50);
    }
    return false;
  };
  const includesOfferFreeUnit = (item) => {
    const unitPrice = Number(item?.original_price ?? item?.price ?? 0);
    return (
      unitPrice > 0 &&
      (item?.line_level_discount_allocations || []).some(
        (allocation) =>
          allocation?.discount_application?.title === offer.discountTitle &&
          Number(allocation?.amount || 0) >= unitPrice
      )
    );
  };
  const appliedItemForGift = (cart, gift) =>
    itemsForGift(cart, gift).find(includesOfferFreeUnit);
  const eligibilitySignature = (cart) => {
    const hush = (cart?.items || [])
      .filter((item) => isHushLine(item) && !isTaggedGift(item))
      .map((item) => {
        const plan = item.selling_plan_allocation?.selling_plan?.id || '';
        return `${item.key}:${item.quantity}:${plan}`;
      })
      .sort()
      .join('|');
    const gifts = (cart?.items || [])
      .filter((item) => offer.gifts.some((gift) => gift.variantId === Number(item.variant_id)))
      .map((item) => {
        const originalLine = Number(item.original_line_price ?? item.line_price ?? 0);
        const finalLine = Number(item.final_line_price ?? item.line_price ?? 0);
        return [
          item.key,
          item.variant_id,
          item.quantity,
          isTaggedGift(item) ? giftRole(item) : 'customer',
          originalLine,
          finalLine
        ].join(':');
      })
      .sort()
      .join('|');
    const discounts = (cart?.cart_level_discount_applications || [])
      .map((discount) => `${discount.title || ''}:${discount.total_allocated_amount || 0}`)
      .sort()
      .join('|');
    return `${hush}::${gifts}::${discounts}`;
  };
  const readUnavailable = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(unavailableKey) || 'null');
      return value && typeof value === 'object' ? value : null;
    } catch (_) {
      return null;
    }
  };
  const markUnavailable = (cart) => {
    try {
      sessionStorage.setItem(
        unavailableKey,
        JSON.stringify({
          signature: eligibilitySignature(cart),
          retryAfter: Date.now() + 5 * 60 * 1000
        })
      );
    } catch (_) {}
  };
  const clearUnavailable = () => {
    try {
      sessionStorage.removeItem(unavailableKey);
    } catch (_) {}
  };

  const setStatus = (state) => {
    const messages = {
      checking: 'Checking your Full Hermetica 5-Formula gift set…',
      applied: 'All 5 bonus full-size formulas (Eternity, Deva, Blue Crush, Cozy & Meru — $216 value) are included free with this Hush order.',
      unavailable: 'This gift set could not be confirmed for your cart. Review your items and prices before checkout.',
      request_error: 'Gift status could not be refreshed. Please try again and review gift prices before checkout.',
      removed: 'The automatic gift set was removed because this cart no longer qualifies.',
      declined: 'Free gifts removed. Your order has only the items you picked.'
    };
    document.querySelectorAll('[data-hsf-gift-offer-status]').forEach((node) => {
      node.dataset.state = state;
      if (messages[state]) node.textContent = messages[state];
    });
    document.querySelectorAll('[data-hsf-offer-marketing]').forEach((node) => {
      node.hidden = state === 'unavailable';
    });
  };

  const dispatchCartUpdate = (cart, action) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: offer.id,
          data: {
            source,
            itemCount: Number(cart?.item_count || 0),
            action
          }
        }
      })
    );
    document.dispatchEvent(
      new CustomEvent('hsf:gift-cart-reconciled', {
        bubbles: true,
        detail: { cart, action, offerId: offer.id }
      })
    );
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart, source, giftOfferHandled: true }
      })
    );
    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart, source, giftOfferHandled: true }
      })
    );
  };

  const removeTaggedLines = async (cart, reason, context) => {
    let current = cart;
    const lines = taggedItems(current);
    if (!lines.length) return { cart: current, mutated: false };

    for (const line of lines) {
      current = await changeLine(line.key, 0);
    }

    emit('gift_remove', {
      placement: context.placement || 'cart',
      reason,
      removed_count: lines.length,
      purchase_type: purchaseType(cart, context.purchase_type),
      ...cartState(current)
    });
    return { cart: current, mutated: true };
  };

  const reconcileOnce = async (cartInput, context = {}) => {
    // Event snapshots can be stale by the time a queued reconciliation runs.
    let cart = await fetchCart();
    let mutated = false;
    const eligible = isEligible(cart, context);
    const placement = context.placement || 'cart_event';
    const mode = purchaseType(cart, context.purchase_type);

    if (!eligible) {
      wasEligible = false;
      clearUnavailable();
      const declined = giftDeclined(cart);
      const removed = await removeTaggedLines(
        cart,
        declined ? 'declined' : isOfferActive() ? 'eligibility_lost' : 'offer_expired',
        context
      );
      cart = removed.cart;
      mutated = removed.mutated;
      if (declined) setStatus('declined');
      if (mutated) {
        if (!declined) setStatus('removed');
        dispatchCartUpdate(cart, declined ? 'declined' : 'removed');
      }
      return { cart, eligible: false, complete: false, mutated };
    }

    const unavailable = readUnavailable();
    if (
      unavailable?.signature === eligibilitySignature(cart) &&
      Number(unavailable.retryAfter || 0) > Date.now()
    ) {
      setStatus('unavailable');
      return { cart, eligible: true, complete: false, mutated: false, error: 'gift_set_backoff' };
    }

    setStatus('checking');
    if (!wasEligible) {
      emit('gift_offer_qualified', {
        placement,
        purchase_type: mode,
        ...cartState(cart)
      });
      wasEligible = true;
    }

    const unexpectedTagged = taggedItems(cart).filter((item) => {
      const matchingGift = offer.gifts.find((gift) => gift.role === giftRole(item));
      return !matchingGift || Number(item.variant_id) !== matchingGift.variantId;
    });
    for (const line of unexpectedTagged) {
      cart = await changeLine(line.key, 0);
      mutated = true;
    }

    for (const gift of offer.gifts) {
      const matchingTagged = taggedItems(cart).filter(
        (item) => giftRole(item) === gift.role && Number(item.variant_id) === gift.variantId
      );
      if (matchingTagged.length > 1) {
        for (const duplicate of matchingTagged.slice(1)) {
          cart = await changeLine(duplicate.key, 0);
          mutated = true;
        }
      }
      const primary = taggedItems(cart).find(
        (item) => giftRole(item) === gift.role && Number(item.variant_id) === gift.variantId
      );
      if (primary && Number(primary.quantity || 0) !== 1) {
        cart = await changeLine(primary.key, 1);
        mutated = true;
      }
    }

    // Preserve shopper-owned exact-SKU lines instead of adding duplicates. Shopify's
    // automatic discount cannot reliably target line properties when identical variants
    // coexist, so an existing line intentionally fulfills the free unit and returns to
    // its normal paid price if Hush is removed; only our tagged lines are auto-removed.
    const missing = offer.gifts.filter(
      (gift) => !itemsForGift(cart, gift).length
    );
    if (missing.length) {
      await addGifts(missing);
      cart = await fetchCart();
      mutated = true;
    }

    const needsDiscountSettlement = (candidateCart) =>
      !offer.gifts.every((gift) => Boolean(appliedItemForGift(candidateCart, gift))) ||
      taggedItems(candidateCart).some((item) => !taggedLineIsFree(item));
    // Automatic discounts can lag the cart mutation on slower Shopify edges.
    // Keep the lines briefly while polling before declaring the set unsafe.
    for (const delay of [150, 350, 750, 1500]) {
      if (!needsDiscountSettlement(cart)) break;
      await wait(delay);
      cart = await fetchCart();
    }

    const paidTaggedCoveredByExistingLine = taggedItems(cart).filter((item) => {
      if (taggedLineIsFree(item)) return false;
      const gift = offer.gifts.find(
        (candidate) => candidate.role === giftRole(item) && candidate.variantId === Number(item.variant_id)
      );
      return Boolean(
        gift &&
        itemsForGift(cart, gift).some(
          (candidate) => candidate !== item && includesOfferFreeUnit(candidate)
        )
      );
    });
    for (const line of paidTaggedCoveredByExistingLine) {
      cart = await changeLine(line.key, 0);
      mutated = true;
    }

    const complete = offer.gifts.every((gift) => Boolean(appliedItemForGift(cart, gift)));
    const unsafeTagged = taggedItems(cart).filter((item) => !taggedLineIsFree(item));
    if (!complete || unsafeTagged.length) {
      const cleaned = await removeTaggedLines(cart, complete ? 'discount_not_applied' : 'incomplete_set', context);
      cart = cleaned.cart;
      mutated = mutated || cleaned.mutated;
      markUnavailable(cart);
      setStatus('unavailable');
      emit('gift_offer_error', {
        placement,
        error_type: complete ? 'discount_not_applied' : 'incomplete_set',
        purchase_type: mode,
        ...cartState(cart)
      });
      if (mutated) dispatchCartUpdate(cart, 'safety_cleanup');
      return { cart, eligible: true, complete: false, mutated, error: 'gift_set_unavailable' };
    }

    setStatus('applied');
    clearUnavailable();
    if (mutated) {
      emit('gift_add_success', {
        placement,
        purchase_type: mode,
        gift_count: offer.gifts.length,
        gift_variant_ids: offer.gifts.map((gift) => String(gift.variantId)).join(','),
        ...cartState(cart)
      });
      dispatchCartUpdate(cart, 'applied');
    }
    return { cart, eligible: true, complete: true, mutated };
  };

  const reconcile = (cartInput, context = {}) => {
    pendingReconciliations += 1;
    updateCheckoutControls();
    const run = () =>
      reconcileOnce(cartInput, context).catch(async (error) => {
        let cart = null;
        let cleanupMutated = false;
        let cleanupFailed = false;
        try {
          cart = await fetchCart();
          const complete = offer.gifts.every((gift) => Boolean(appliedItemForGift(cart, gift)));
          const unsafe = taggedItems(cart).some((item) => !taggedLineIsFree(item));
          if (isEligible(cart, context) && complete && !unsafe) {
            clearUnavailable();
            setStatus('applied');
            dispatchCartUpdate(cart, 'request_error_recovered');
            return { cart, eligible: true, complete: true, mutated: false, recovered: true };
          }
          const cleaned = await removeTaggedLines(cart, 'request_error_cleanup', context);
          cart = cleaned.cart;
          cleanupMutated = cleaned.mutated;
        } catch (_) {
          cleanupFailed = true;
        }
        const unavailableGift = error?.message === 'gift_unavailable';
        if (unavailableGift && cart) markUnavailable(cart);
        setStatus(unavailableGift ? 'unavailable' : 'request_error');
        emit('gift_offer_error', {
          placement: context.placement || 'cart_event',
          error_type: error?.message || 'request_failed',
          purchase_type: purchaseType(cart, context.purchase_type),
          ...cartState(cart)
        });
        if (cleanupMutated) {
          dispatchCartUpdate(cart, unavailableGift ? 'gift_unavailable_cleanup' : 'request_error_cleanup');
        }
        return {
          cart,
          eligible: Boolean(cart && isEligible(cart, context)),
          complete: false,
          mutated: cleanupMutated,
          error: cleanupFailed ? 'gift_cleanup_unconfirmed' : (unavailableGift ? 'gift_unavailable' : 'request_failed')
        };
      });

    const result = reconciliationTail.then(run, run).then((value) => {
      lastCart = value.cart;
      return value;
    }).finally(() => {
      pendingReconciliations -= 1;
      updateCheckoutControls();
    });
    reconciliationTail = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  };

  const prepareCheckout = async () => {
    if (!await waitForNativeCart()) return { safe: false, error: 'native_cart_pending' };
    // The most recently edited note is authoritative; a hidden drawer may contain
    // a stale duplicate textarea, so do not pick the first DOM note arbitrarily.
    const desiredNote = latestNoteDraft;
    const initialRevision = nativeMutationRevision;
    // Queue behind in-flight additions/discount polling, then reconcile from a new
    // Shopify snapshot and independently verify the final cart before navigation.
    const result = await reconcile(null, { placement: 'checkout_guard' });
    if (result.error === 'request_failed' || result.error === 'gift_cleanup_unconfirmed') {
      return { safe: false, error: result.error };
    }
    try {
      if (!await waitForNativeCart()) return { safe: false, error: 'native_cart_pending' };
      if (initialRevision !== nativeMutationRevision) return { safe: false, error: 'cart_changed_during_verification' };
      // Preserve the form's note even if its earlier debounced save failed. Do not
      // repost positional quantity inputs after gift additions/removals.
      if (desiredNote !== null && desiredNote !== (result.cart?.note || '')) {
        await cartRequest('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ note: desiredNote })
        });
      }
      const cart = await fetchCart();
      lastCart = cart;
      const safe = pendingReconciliations === 0 && pendingNativeMutations === 0 &&
        initialRevision === nativeMutationRevision && cartSafeForCheckout(cart) &&
        (desiredNote === null || desiredNote === (cart.note || ''));
      return { safe, cart, error: safe ? null : 'cart_changed_or_paid_automatic_gift' };
    } catch (_) {
      return { safe: false, error: 'checkout_cart_unverified' };
    }
  };

  const guardedCheckout = async (proceed) => {
    if (checkoutAttempt) return;
    checkoutAttempt = true;
    checkoutMessage('Checking your cart and gift prices…');
    updateCheckoutControls();
    try {
      const result = await prepareCheckout();
      if (!result.safe) {
        checkoutMessage(result.error === 'native_cart_pending' || result.error === 'cart_changed_during_verification'
          ? 'Your cart is still updating. Please try Checkout again after the update finishes.'
          : 'We could not verify your gift prices. Please try Checkout again, or remove the automatic gift items from your cart.');
        return;
      }
      checkoutMessage('');
      proceed();
    } catch (_) {
      checkoutMessage('Your cart could not be checked. Please try Checkout again when your connection is available.');
    } finally {
      checkoutAttempt = false;
      updateCheckoutControls();
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitter = event.submitter;
    if (form?.id !== 'cart-form' || submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    guardedCheckout(() => {
      // The old form can still contain positional updates[] inputs while Shopify
      // refreshes its sections. Never repost them after verifying a changed cart.
      // Quantity and note edits already use the theme's AJAX cart endpoints.
      window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
    });
  }, true);

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !/\/checkout(?:\/|$)/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    guardedCheckout(() => window.location.assign(url.href));
  }, true);

  const observeOfferViews = () => {
    if (!analyticsEnabled) return;
    const nodes = Array.from(
      document.querySelectorAll('[data-hsf-hush-gift-offer]:not([data-hsf-offer-view-bound])')
    );
    if (!nodes.length) return;
    nodes.forEach((node) => {
      node.dataset.hsfOfferViewBound = 'true';
    });
    let viewed = [];
    try {
      viewed = JSON.parse(sessionStorage.getItem(viewedKey) || '[]');
      if (!Array.isArray(viewed)) viewed = [];
    } catch (_) {
      viewed = [];
    }

    const record = (node) => {
      const placement = node.dataset.hsfOfferPlacement || 'unknown';
      if (viewed.includes(placement)) return;
      viewed.push(placement);
      try {
        sessionStorage.setItem(viewedKey, JSON.stringify(viewed));
      } catch (_) {}
      emit('gift_offer_view', { placement });
    };

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(record);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          record(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.25 }
    );
    nodes.forEach((node) => observer.observe(node));
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-hsf-gift-offer-link]');
    if (!link) return;
    emit('gift_offer_click', {
      placement: link.closest('[data-hsf-hush-gift-offer]')?.dataset.hsfOfferPlacement || 'unknown'
    });
  });

  ['cart:update', 'cart:updated', 'cart:refresh'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event.detail?.data?.source === source || event.detail?.source === source) return;
      if (event.detail?.data?.didError) return;
      if (event.detail?.giftOfferHandled) {
        window.clearTimeout(eventTimer);
        return;
      }
      const suppliedCart = event.detail?.resource || event.detail?.cart || null;
      const suppliedCartIsRelevant =
        Boolean(suppliedCart?.items) && (hasHush(suppliedCart) || taggedItems(suppliedCart).length > 0);
      const hushProductEvent =
        hushProductIds.some((id) => String(event.detail?.data?.productId || '') === String(id));
      const explicitOfferEvent = Boolean(event.detail?.giftOfferPlacement);
      if (
        !suppliedCartIsRelevant &&
        !hushProductEvent &&
        !explicitOfferEvent &&
        !isCartSurface() &&
        !isHushSurface()
      ) {
        return;
      }
      window.clearTimeout(eventTimer);
      eventTimer = window.setTimeout(() => {
        reconcile(suppliedCart, {
          placement: event.detail?.giftOfferPlacement || 'cart_event',
          purchase_type: event.detail?.purchaseType || ''
        });
      }, 40);
    });
  });

  // "No thanks, remove the free gifts" and "Add the free gifts back" in the cart.
  const setDeclined = async (declined, button) => {
    if (button) button.disabled = true;
    try {
      await cartRequest('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: { [declineAttribute]: declined ? offer.id : '' } })
      });
      emit(declined ? 'gift_offer_declined' : 'gift_offer_restored', { placement: 'cart' });
      const result = await reconcile(null, { placement: declined ? 'cart_decline' : 'cart_restore' });
      // Re-render the cart even when nothing was added or removed, so the button flips.
      if (!result?.mutated) dispatchCartUpdate(result?.cart || await fetchCart(), declined ? 'declined' : 'restored');
    } catch (_) {
      checkoutMessage('That did not go through. Please try again.');
    } finally {
      if (button) button.disabled = false;
    }
  };
  document.addEventListener('click', (event) => {
    const decline = event.target.closest('[data-hsf-gift-decline]');
    const restore = event.target.closest('[data-hsf-gift-restore]');
    if (!decline && !restore) return;
    event.preventDefault();
    setDeclined(Boolean(decline), decline || restore);
  });

  window.HermeticaHushGiftOffer = Object.freeze({
    offer,
    reconcile,
    prepareCheckout,
    beginCartMutation,
    isOfferActive,
    isTaggedGift
  });

  const init = () => {
    observeOfferViews();
    updateCheckoutControls();
    if ('MutationObserver' in window && document.body) {
      const mutationObserver = new MutationObserver((mutations) => {
        const hasOfferNode = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node instanceof Element &&
              (node.matches('[data-hsf-hush-gift-offer], [data-hsf-gift-checkout], [data-hsf-gift-accelerated]') ||
                node.querySelector('[data-hsf-hush-gift-offer], [data-hsf-gift-checkout], [data-hsf-gift-accelerated]'))
          )
        );
        if (!hasOfferNode) return;
        window.clearTimeout(viewMutationTimer);
        viewMutationTimer = window.setTimeout(() => {
          observeOfferViews();
          updateCheckoutControls();
        }, 0);
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }
    if (isCartSurface() || isHushSurface()) {
      reconcile(null, { placement: isCartSurface() ? 'cart_page' : 'hush_page_init' });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

